import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CalendarDays, CheckCircle, ChevronLeft, ChevronRight, Circle, Clock, Globe, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar } from '../ui/calendar';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';

const DEFAULT_LAT_LNG = { lat: 14.5883136, lng: 121.044992 };
const DEFAULT_MAP_ZOOM = 14;

const formatCoord = (value) => Number(value).toFixed(6);

const buildGoogleMapsLinkFromLatLng = (lat, lng) =>
  `https://www.google.com/maps?q=${encodeURIComponent(`${formatCoord(lat)},${formatCoord(lng)}`)}`;

let leafletLoaderPromise = null;

const loadLeafletFromCdn = () => {
  if (leafletLoaderPromise) {
    return leafletLoaderPromise;
  }

  leafletLoaderPromise = new Promise((resolve, reject) => {
    if (window.L) {
      resolve(window.L);
      return;
    }

    const existingStyle = document.querySelector('link[data-leaflet-cdn="true"]');
    if (!existingStyle) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      link.dataset.leafletCdn = 'true';
      document.head.appendChild(link);
    }

    const existingScript = document.querySelector('script[data-leaflet-cdn="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.L));
      existingScript.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.async = true;
    script.dataset.leafletCdn = 'true';
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.body.appendChild(script);
  });

  return leafletLoaderPromise;
};

const parseLatLngFromLocation = (value) => {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) {
    return null;
  }

  const coordinateMatch = normalizedValue.match(/@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/i);
  if (coordinateMatch) {
    const lat = Number(coordinateMatch[1]);
    const lng = Number(coordinateMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng };
    }
  }

  try {
    const parsedUrl = new URL(normalizedValue);
    const qValue = parsedUrl.searchParams.get('q') || parsedUrl.searchParams.get('query');
    if (qValue) {
      const qMatch = String(qValue).match(/(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)/);
      if (qMatch) {
        const lat = Number(qMatch[1]);
        const lng = Number(qMatch[2]);
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          return { lat, lng };
        }
      }
    }
  } catch (error) {
    const rawMatch = normalizedValue.match(/(-?\d+(?:\.\d+)?)[,\s]+(-?\d+(?:\.\d+)?)/);
    if (rawMatch) {
      const lat = Number(rawMatch[1]);
      const lng = Number(rawMatch[2]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return { lat, lng };
      }
    }
  }

  return null;
};

const formatDateForValue = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (value) => {
  if (!value) {
    return 'Select date';
  }

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) {
    return 'Select date';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const parseConfirmedAppointmentTime = (value) => {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{1,2})\s*:\s*(\d{1,2})\s*(AM|PM)$/i);
  if (!match) {
    return { hour: '', minute: '00', period: 'AM' };
  }

  const hour = String(Number(match[1]));
  const minute = String(Number(match[2])).padStart(2, '0');
  const period = String(match[3] || 'AM').toUpperCase() === 'PM' ? 'PM' : 'AM';

  if (!hour || Number(hour) < 1 || Number(hour) > 12) {
    return { hour: '', minute: '00', period: 'AM' };
  }

  if (Number(minute) < 0 || Number(minute) > 59) {
    return { hour: '', minute: '00', period: 'AM' };
  }

  return { hour, minute, period };
};

const buildConfirmedAppointmentTime = ({ hour, minute, period }) => {
  const trimmedHour = String(hour || '').trim();
  const trimmedMinute = String(minute || '').trim();
  const trimmedPeriod = String(period || '').trim().toUpperCase();

  if (!trimmedHour) {
    return '';
  }

  const safeMinute = `${Number(trimmedMinute)}`.padStart(2, '0');
  const safePeriod = trimmedPeriod === 'PM' ? 'PM' : 'AM';
  return `${trimmedHour}:${safeMinute} ${safePeriod}`;
};

const formatShortDateForDisplay = (value) => {
  if (!value) {
    return '';
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  }).format(date);
};

const formatLongDateForDisplay = (value) => {
  if (!value) {
    return '';
  }

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: '2-digit',
    year: 'numeric',
  }).format(date);
};

const startOfWeek = (value) => {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return new Date();
  }

  const start = new Date(date);
  const day = start.getDay();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - day);
  return start;
};

const addDays = (value, amount) => {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return new Date();
  }

  date.setDate(date.getDate() + amount);
  return date;
};

const addWeeks = (value, amount) => addDays(value, amount * 7);

const startOfMonth = (value) => {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return new Date();
  }

  return new Date(date.getFullYear(), date.getMonth(), 1);
};

const endOfMonth = (value) => {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return new Date();
  }

  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

const addMonths = (value, amount) => {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return new Date();
  }

  date.setMonth(date.getMonth() + amount);
  return date;
};

const formatMonthYearLabel = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
  }).format(date);
};

const formatWeekRangeLabel = (value) => {
  const start = startOfWeek(value);
  const end = addDays(start, 6);
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();

  if (sameMonth) {
    return `${new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
    }).format(start)} - ${new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      year: 'numeric',
    }).format(end)}`;
  }

  return `${new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
  }).format(start)} - ${new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(end)}`;
};

const formatDayTimelineLabel = (hour) => {
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const normalizedHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${normalizedHour}:00 ${suffix}`;
};

const getEmailUsername = (email) => {
  const normalizedEmail = String(email || '').trim();
  if (!normalizedEmail) {
    return '';
  }

  return normalizedEmail.split('@')[0] || '';
};

const parseTimeToMinutes = (value) => {
  const match = String(value || '').trim().match(/^(\d{1,2})\s*:\s*(\d{1,2})\s*(AM|PM)$/i);
  if (!match) {
    return null;
  }

  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const period = String(match[3] || '').toUpperCase();

  if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour < 1 || hour > 12 || minute < 0 || minute > 59) {
    return null;
  }

  if (period === 'PM' && hour !== 12) {
    hour += 12;
  }
  if (period === 'AM' && hour === 12) {
    hour = 0;
  }

  return (hour * 60) + minute;
};

const createInitialIteneraryForm = (dateValue) => ({
  date: dateValue,
  accountName: '',
  confirmedAppointmentHour: '',
  confirmedAppointmentMinute: '00',
  confirmedAppointmentPeriod: 'AM',
  address: '',
  location: '',
  priorities: [''],
  todoItems: [''],
});

export default function ItenerariesSubmenu({ axiosInstance, employees = [], user = {} }) {
  const locationMapContainerRef = useRef(null);
  const locationLeafletRef = useRef({
    map: null,
    marker: null,
  });
  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [addedEmployees, setAddedEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showAllItineraries, setShowAllItineraries] = useState(false);
  const [calendarViewMode, setCalendarViewMode] = useState('month');
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [activeCalendarDate, setActiveCalendarDate] = useState(null);
  const [isDateAccountsModalOpen, setIsDateAccountsModalOpen] = useState(false);
  const [editingIteneraryId, setEditingIteneraryId] = useState(null);
  const [editingSelectedEmployeeId, setEditingSelectedEmployeeId] = useState(null);
  const [iteneraryRecords, setIteneraryRecords] = useState([]);
  const [isLoadingIteneraries, setIsLoadingIteneraries] = useState(false);
  const [isIteneraryModalOpen, setIsIteneraryModalOpen] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isAppointmentTimePickerOpen, setIsAppointmentTimePickerOpen] = useState(false);
  const [locationDraft, setLocationDraft] = useState('');
  const [pinnedLatLng, setPinnedLatLng] = useState(null);
  const [isSavingItenerary, setIsSavingItenerary] = useState(false);
  const [iteneraryForm, setIteneraryForm] = useState(() => createInitialIteneraryForm(formatDateForValue(new Date())));
  const [iteneraryRecordStatesById, setIteneraryRecordStatesById] = useState({});
  const [isLoadingIteneraryStates, setIsLoadingIteneraryStates] = useState(false);
  const [recordsFilterDate, setRecordsFilterDate] = useState(() => new Date());
  const [isCancelledRecordsModalOpen, setIsCancelledRecordsModalOpen] = useState(false);
  const [recordsSelectedDates, setRecordsSelectedDates] = useState([]);
  const [isRecordsMultiDatePickerOpen, setIsRecordsMultiDatePickerOpen] = useState(false);
  const [reportsMode, setReportsMode] = useState('daily');
  const [isReportsCalendarOpen, setIsReportsCalendarOpen] = useState(false);
  const [selectedStatusDetail, setSelectedStatusDetail] = useState(null);

  useEffect(() => {
    if (!axiosInstance) {
      return undefined;
    }

    let isMounted = true;

    const fetchSelectedEmployees = async () => {
      try {
        const response = await axiosInstance.get('/itineraries-selected-employees');
        if (isMounted) {
          const nextEmployees = Array.isArray(response.data) ? response.data : [];
          setAddedEmployees(nextEmployees);
          setSelectedEmployee((currentSelected) => {
            if (!currentSelected) {
              return null;
            }

            const stillExists = nextEmployees.find((employee) => String(employee.id) === String(currentSelected.id));
            return stillExists || null;
          });
        }
      } catch (error) {
        console.error('Error fetching Iteneraries selected employees:', error);
        toast.error('Failed to load selected employees');
      }
    };

    fetchSelectedEmployees();

    return () => {
      isMounted = false;
    };
  }, [axiosInstance]);

  useEffect(() => {
    if (!axiosInstance || (!selectedEmployee?.id && !showAllItineraries)) {
      setIteneraryRecords([]);
      return undefined;
    }

    let isMounted = true;
    setIsLoadingIteneraries(true);

    const fetchIteneraries = async () => {
      try {
        const response = showAllItineraries
          ? await axiosInstance.get('/itineraries-records-all')
          : await axiosInstance.get('/itineraries-records', {
              params: { selected_employee_id: selectedEmployee.id },
            });

        if (isMounted) {
          setIteneraryRecords(Array.isArray(response.data) ? response.data : []);
        }
      } catch (error) {
        console.error('Error fetching Iteneraries records:', error);
        toast.error('Failed to load itineraries');
        if (isMounted) {
          setIteneraryRecords([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingIteneraries(false);
        }
      }
    };

    fetchIteneraries();

    return () => {
      isMounted = false;
    };
  }, [axiosInstance, selectedEmployee?.id, showAllItineraries]);

  const activeEmployees = useMemo(
    () =>
      (Array.isArray(employees) ? employees : [])
        .filter((employee) => Boolean(employee?.is_active))
        .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '')),
    [employees]
  );

  const addedEmployeeIds = useMemo(
    () => new Set(addedEmployees.map((employee) => String(employee.employee_ref || employee.id))),
    [addedEmployees]
  );

  const filteredEmployees = useMemo(() => {
    const query = employeeSearchQuery.trim().toLowerCase();

    return activeEmployees.filter((employee) => {
      if (!query) {
        return true;
      }

      return (
        employee.full_name?.toLowerCase().includes(query) ||
        employee.employee_id?.toLowerCase().includes(query) ||
        employee.email?.toLowerCase().includes(query)
      );
    });
  }, [activeEmployees, employeeSearchQuery]);

  const employeeUsernameBySelectedId = useMemo(() => {
    const map = new Map();

    addedEmployees.forEach((employee) => {
      const employeeId = String(employee?.id || '').trim();
      if (!employeeId) {
        return;
      }

      map.set(employeeId, getEmailUsername(employee?.email));
    });

    return map;
  }, [addedEmployees]);

  const formatAccountDisplayName = useCallback(
    (record) => {
      const accountName = String(record?.account_name || record?.accountName || '').trim() || 'Unnamed account';
      if (!showAllItineraries) {
        return accountName;
      }

      const ownerUsername = employeeUsernameBySelectedId.get(String(record?.selected_employee_id || '').trim()) || '';
      if (!ownerUsername) {
        return accountName;
      }

      return `(${ownerUsername}) - ${accountName}`;
    },
    [employeeUsernameBySelectedId, showAllItineraries]
  );

  const iteneraryAccountsByDate = useMemo(() => {
    const map = new Map();

    (Array.isArray(iteneraryRecords) ? iteneraryRecords : []).forEach((record) => {
      const dateKey = record?.date ? String(record.date) : '';
      const accountName = formatAccountDisplayName(record);

      if (!dateKey || !accountName) {
        return;
      }

      const current = map.get(dateKey) || [];
      current.push(String(accountName));
      map.set(dateKey, current);
    });

    return map;
  }, [formatAccountDisplayName, iteneraryRecords]);

  const calendarDayContent = useCallback(
    ({ date }) => {
      const dateKey = formatDateForValue(date);
      const accounts = iteneraryAccountsByDate.get(dateKey) || [];

      return (
        <div className="flex h-full max-h-full w-full min-h-0 flex-col items-start justify-start overflow-hidden">
          <div className="shrink-0 text-base font-medium">{date.getDate()}</div>
          {accounts.length > 0 ? (
            <div className="mt-1 min-h-0 flex-1 w-full overflow-y-auto text-[9px] leading-tight text-gray-600 dark:text-gray-300">
              {accounts.map((account, index) => (
                <div key={`${dateKey}-${index}`} className="break-words whitespace-normal">
                  {account}
                </div>
              ))}
            </div>
          ) : (
            <div className="min-h-0 flex-1" />
          )}
        </div>
      );
    },
    [iteneraryAccountsByDate]
  );

  const activeCalendarDateKey = useMemo(
    () => (activeCalendarDate ? formatDateForValue(activeCalendarDate) : ''),
    [activeCalendarDate]
  );

  const activeCalendarDateRecords = useMemo(() => {
    if (!activeCalendarDateKey) {
      return [];
    }

    const records = (Array.isArray(iteneraryRecords) ? iteneraryRecords : []).filter(
      (record) => String(record?.date || '') === String(activeCalendarDateKey)
    );

    return records.sort((a, b) => String(a?.created_at || '').localeCompare(String(b?.created_at || '')));
  }, [activeCalendarDateKey, iteneraryRecords]);

  const currentDateKey = useMemo(() => formatDateForValue(new Date()), []);
  const recordsFilterDateKey = useMemo(() => formatDateForValue(recordsFilterDate), [recordsFilterDate]);
  const reportsModeButtonLabel = useMemo(() => {
    if (reportsMode === 'weekly') {
      return 'Weekly Report';
    }
    if (reportsMode === 'monthly') {
      return 'Monthly Report';
    }
    return 'Daily Report';
  }, [reportsMode]);

  const reportsRange = useMemo(() => {
    if (reportsMode === 'weekly') {
      const from = startOfWeek(recordsFilterDate);
      return { from, to: addDays(from, 6) };
    }
    if (reportsMode === 'monthly') {
      return { from: startOfMonth(recordsFilterDate), to: endOfMonth(recordsFilterDate) };
    }
    return { from: recordsFilterDate, to: recordsFilterDate };
  }, [recordsFilterDate, reportsMode]);

  const reportsPeriodLabel = useMemo(() => {
    if (reportsMode === 'weekly') {
      return formatWeekRangeLabel(recordsFilterDate);
    }
    if (reportsMode === 'monthly') {
      return formatMonthYearLabel(recordsFilterDate);
    }
    return formatLongDateForDisplay(recordsFilterDate);
  }, [recordsFilterDate, reportsMode]);

  const selectedRecordDateKeys = useMemo(
    () => (Array.isArray(recordsSelectedDates) ? recordsSelectedDates.map((date) => formatDateForValue(date)).filter(Boolean) : []),
    [recordsSelectedDates]
  );

  const currentDateDisplay = useMemo(
    () => {
      const parts = new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).formatToParts(new Date());

      const partMap = parts.reduce((acc, part) => {
        if (part.type !== 'literal') {
          acc[part.type] = part.value;
        }
        return acc;
      }, {});

      const month = partMap.month || '';
      const day = partMap.day || '';
      const year = partMap.year || '';
      const weekday = partMap.weekday || '';

      return `${month} ${day}, ${year} ${weekday}`.trim();
    },
    []
  );

  const currentDateIteneraries = useMemo(() => {
    if (!currentDateKey) {
      return [];
    }

    const records = (Array.isArray(iteneraryRecords) ? iteneraryRecords : []).filter(
      (record) => String(record?.date || '') === String(currentDateKey)
    );

    return records.sort((a, b) => String(a?.created_at || '').localeCompare(String(b?.created_at || '')));
  }, [currentDateKey, iteneraryRecords]);

  const iteneraryRecordsByDate = useMemo(() => {
    const map = new Map();

    (Array.isArray(iteneraryRecords) ? iteneraryRecords : []).forEach((record) => {
      const dateKey = String(record?.date || '').trim();
      if (!dateKey) {
        return;
      }

      const current = map.get(dateKey) || [];
      current.push(record);
      map.set(dateKey, current);
    });

    map.forEach((records, key) => {
      map.set(
        key,
        [...records].sort((a, b) => String(a?.created_at || '').localeCompare(String(b?.created_at || '')))
      );
    });

    return map;
  }, [iteneraryRecords]);

  const calendarViewButtonLabel = useMemo(() => {
    if (calendarViewMode === 'week') {
      return 'Week View';
    }
    if (calendarViewMode === 'day') {
      return 'Day View';
    }
    return 'Month View';
  }, [calendarViewMode]);

  const calendarHeaderLabel = useMemo(() => {
    if (calendarViewMode === 'week') {
      return formatWeekRangeLabel(selectedDate);
    }
    if (calendarViewMode === 'day') {
      return formatDateForDisplay(formatDateForValue(selectedDate));
    }
    return formatMonthYearLabel(selectedDate);
  }, [calendarViewMode, selectedDate]);

  const weekViewDates = useMemo(() => {
    const weekStart = startOfWeek(selectedDate);
    return Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
  }, [selectedDate]);

  const dayViewDateKey = useMemo(() => formatDateForValue(selectedDate), [selectedDate]);

  const dayViewRecords = useMemo(
    () => iteneraryRecordsByDate.get(dayViewDateKey) || [],
    [dayViewDateKey, iteneraryRecordsByDate]
  );

  const dayViewTimedRecords = useMemo(() => {
    const timeline = Array.from({ length: 24 }, (_, hour) => ({
      hour,
      items: [],
    }));
    const unscheduled = [];

    dayViewRecords.forEach((record) => {
      const minutes = parseTimeToMinutes(record?.confirmed_appointment_by_client);
      if (minutes === null) {
        unscheduled.push(record);
        return;
      }

      const slotIndex = Math.min(23, Math.max(0, Math.floor(minutes / 60)));
      timeline[slotIndex].items.push(record);
    });

    return { timeline, unscheduled };
  }, [dayViewRecords]);

  const reportsFilteredIteneraries = useMemo(() => {
    const fromKey = formatDateForValue(reportsRange.from);
    const toKey = formatDateForValue(reportsRange.to);
    if (!fromKey || !toKey) {
      return [];
    }

    const records = (Array.isArray(iteneraryRecords) ? iteneraryRecords : []).filter((record) => {
      const recordDate = String(record?.date || '').trim();
      return recordDate >= fromKey && recordDate <= toKey;
    });

    return records.sort((a, b) => String(a?.created_at || '').localeCompare(String(b?.created_at || '')));
  }, [iteneraryRecords, reportsRange.from, reportsRange.to]);

  const reportsMetrics = useMemo(() => {
    const records = Array.isArray(reportsFilteredIteneraries) ? reportsFilteredIteneraries : [];
    const accountNames = records
      .map((record) => String(record?.account_name || record?.accountName || '').trim())
      .filter(Boolean);

    let totalPriorities = 0;
    let totalTodoItems = 0;
    let cancelledCount = 0;
    let finishedCount = 0;
    const cancelledItems = [];

    records.forEach((record) => {
      const priorities = (Array.isArray(record?.priorities) ? record.priorities : [])
        .map((item) => String(item || '').trim())
        .filter(Boolean);

      const todoItems = (
        Array.isArray(record?.todo_items)
          ? record.todo_items
          : Array.isArray(record?.todoItems)
            ? record.todoItems
            : []
      )
        .map((item) => String(item || '').trim())
        .filter(Boolean);

      totalPriorities += priorities.length;
      totalTodoItems += todoItems.length;

      const state = iteneraryRecordStatesById?.[String(record?.id || '')] || {};
      const statusCancelled = Boolean(state?.status_cancelled);
      const statusFinished = Boolean(state?.status_finished);
      const cancellationReason = String(state?.cancellation_reason || '').trim();
      if (statusCancelled) {
        cancelledCount += 1;
        cancelledItems.push({
          id: String(record?.id || ''),
          date: String(record?.date || ''),
          accountName: formatAccountDisplayName(record),
          reason: cancellationReason,
        });
      } else if (statusFinished) {
        finishedCount += 1;
      }
    });

    const totalStatusCount = finishedCount + cancelledCount;
    const finishedPct = totalStatusCount > 0 ? (finishedCount / totalStatusCount) * 100 : 0;
    const cancelledPct = totalStatusCount > 0 ? (cancelledCount / totalStatusCount) * 100 : 0;

    return {
      accountCount: accountNames.length,
      totalPriorities,
      totalTodoItems,
      cancelledCount,
      cancelledItems,
      finishedPct,
      cancelledPct,
      finishedCount,
    };
  }, [formatAccountDisplayName, iteneraryRecordStatesById, reportsFilteredIteneraries]);

  const reportsPieData = useMemo(
    () => [
      { name: 'Finished', value: reportsMetrics.finishedCount },
      { name: 'Cancelled', value: reportsMetrics.cancelledCount },
    ],
    [reportsMetrics.cancelledCount, reportsMetrics.finishedCount]
  );

  const reportsPieTotal = useMemo(
    () => reportsMetrics.finishedCount + reportsMetrics.cancelledCount,
    [reportsMetrics.cancelledCount, reportsMetrics.finishedCount]
  );

  const reportsChartData = useMemo(() => {
    if (reportsPieTotal > 0) {
      return reportsPieData;
    }

    return [{ name: 'No Data', value: 1 }];
  }, [reportsPieData, reportsPieTotal]);

  const reportsChartKey = useMemo(
    () => `${reportsMode}-${reportsPieTotal}-${reportsMetrics.finishedCount}-${reportsMetrics.cancelledCount}`,
    [reportsMetrics.cancelledCount, reportsMetrics.finishedCount, reportsMode, reportsPieTotal]
  );

  const allRecordsIteneraries = useMemo(() => (
    [...(Array.isArray(iteneraryRecords) ? iteneraryRecords : [])].sort((a, b) => {
      const dateCompare = String(b?.date || '').localeCompare(String(a?.date || ''));
      if (dateCompare !== 0) {
        return dateCompare;
      }
      return String(b?.created_at || '').localeCompare(String(a?.created_at || ''));
    })
  ), [iteneraryRecords]);

  const filteredRecordsIteneraries = useMemo(() => {
    if (selectedRecordDateKeys.length === 0) {
      return allRecordsIteneraries;
    }

    const selectedKeys = new Set(selectedRecordDateKeys);
    return allRecordsIteneraries.filter((record) => selectedKeys.has(String(record?.date || '').trim()));
  }, [allRecordsIteneraries, selectedRecordDateKeys]);

  const renderTaskStateList = (items, stateItems, keyPrefix) => {
    const list = (Array.isArray(items) ? items : [])
      .map((item) => String(item || '').trim())
      .filter(Boolean);

    if (list.length === 0) {
      return '—';
    }

    const normalizedStateItems = Array.isArray(stateItems) ? stateItems : [];

    return (
      <div className="space-y-1">
        {list.map((item, index) => {
          const finished = Boolean(normalizedStateItems[index]?.finished);
          return (
            <div
              key={`${keyPrefix}-${index}`}
              className={`break-words ${finished ? 'text-green-600 font-semibold' : 'text-red-600'}`}
            >
              {item}
            </div>
          );
        })}
      </div>
    );
  };

  const openStatusDetail = (record) => {
    const state = iteneraryRecordStatesById?.[String(record?.id || '')] || {};
    const cancelled = Boolean(state?.status_cancelled);
    const finished = Boolean(state?.status_finished);
    const content = cancelled
      ? String(state?.cancellation_reason || '').trim()
      : finished
        ? String(state?.itinerary_outcome || '').trim()
        : '';

    if (!content) {
      return;
    }

    setSelectedStatusDetail({
      title: cancelled ? 'Reason of Cancellation' : 'Itinerary Outcome',
      date: record?.date ? formatLongDateForDisplay(`${record.date}T00:00:00`) : '',
      accountName: formatAccountDisplayName(record),
      content,
    });
  };

  const renderStatusValue = (record, { clickable = false } = {}) => {
    const state = iteneraryRecordStatesById?.[String(record?.id || '')] || {};
    const finished = Boolean(state?.status_finished);
    const cancelled = Boolean(state?.status_cancelled);
    const canOpenDetail = clickable && (cancelled || finished);
    const className = cancelled
      ? 'text-red-600 font-bold'
      : finished
        ? 'text-green-600 font-bold'
        : 'text-gray-400 font-bold';
    const label = cancelled ? 'Cancelled' : finished ? 'Finished' : 'On Queue';

    if (!canOpenDetail) {
      return <span className={className}>{label}</span>;
    }

    return (
      <button
        type="button"
        onClick={() => openStatusDetail(record)}
        className={`${className} underline-offset-2 hover:underline`}
      >
        {label}
      </button>
    );
  };

  const renderItineraryStatusTable = (records, { showDate = false, statusClickable = false, showActions = false } = {}) => {
    const gridClassName = showDate
      ? showActions
        ? 'grid-cols-9'
        : 'grid-cols-8'
      : showActions
        ? 'grid-cols-8'
        : 'grid-cols-7';

    return (
    <div className="space-y-3">
      <div className={`grid ${gridClassName} gap-2`}>
        {showDate ? (
          <div className="px-2 py-2 text-center text-xs font-bold text-gray-900 dark:text-white">
            Date
          </div>
        ) : null}
        <div className="px-2 py-2 text-center text-xs font-bold text-gray-900 dark:text-white">
          Account Name
        </div>
        <div className="px-2 py-2 text-center text-xs font-bold text-gray-900 dark:text-white">
          Appointment Time
        </div>
        <div className="px-2 py-2 text-center text-xs font-bold text-gray-900 dark:text-white">
          Address
        </div>
        <div className="px-2 py-2 text-center text-xs font-bold text-gray-900 dark:text-white">
          Location
        </div>
        <div className="flex flex-col items-center gap-1 px-2 py-2 text-center text-xs font-bold text-gray-900 dark:text-white">
          <div>Priority Task</div>
          <div className="flex items-center justify-center gap-4 text-[10px] font-medium text-gray-700 dark:text-gray-300">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              <span>Finished</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              <span>Unfinish</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center gap-1 px-2 py-2 text-center text-xs font-bold text-gray-900 dark:text-white">
          <div>To-Do List</div>
          <div className="flex items-center justify-center gap-4 text-[10px] font-medium text-gray-700 dark:text-gray-300">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-green-500" />
              <span>Finished</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-red-500" />
              <span>Unfinish</span>
            </div>
          </div>
        </div>
        <div className="px-2 py-2 text-center text-xs font-bold text-gray-900 dark:text-white">
          Status
        </div>
        {showActions ? (
          <div className="px-2 py-2 text-center text-xs font-bold text-gray-900 dark:text-white">
            Action
          </div>
        ) : null}
      </div>
      {records.map((record) => {
        const state = iteneraryRecordStatesById?.[String(record?.id || '')] || {};
        const todoItems = Array.isArray(record?.todo_items)
          ? record.todo_items
          : Array.isArray(record?.todoItems)
            ? record.todoItems
            : [];

        return (
          <div
            key={`${record?.id || `${record?.date}-${record?.account_name || record?.accountName || ''}`}`}
            className={`grid ${gridClassName} gap-2`}
          >
            {showDate ? (
              <div className="rounded-lg border border-gray-200 bg-white p-2 text-xs text-gray-900 shadow-sm dark:border-gray-700 dark:bg-slate-900/50 dark:text-white">
                {record?.date ? formatLongDateForDisplay(`${record.date}T00:00:00`) : '—'}
              </div>
            ) : null}
            <div className="rounded-lg border border-gray-200 bg-white p-2 text-xs text-gray-900 shadow-sm dark:border-gray-700 dark:bg-slate-900/50 dark:text-white">
              {record?.account_name || record?.accountName || '—'}
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-2 text-xs text-gray-900 shadow-sm dark:border-gray-700 dark:bg-slate-900/50 dark:text-white">
              {record?.confirmed_appointment_by_client || '—'}
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-2 text-xs text-gray-900 shadow-sm dark:border-gray-700 dark:bg-slate-900/50 dark:text-white">
              {record?.address || '—'}
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-2 text-xs text-gray-900 shadow-sm dark:border-gray-700 dark:bg-slate-900/50 dark:text-white">
              {record?.location ? (
                <a
                  href={record.location}
                  target="_blank"
                  rel="noreferrer"
                  className="break-words text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-300"
                >
                  {record.location}
                </a>
              ) : (
                '—'
              )}
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-2 text-xs text-gray-900 shadow-sm dark:border-gray-700 dark:bg-slate-900/50 dark:text-white">
              {renderTaskStateList(record?.priorities, state?.priority_items, `${record?.id || 'priority'}-priority`)}
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-2 text-xs text-gray-900 shadow-sm dark:border-gray-700 dark:bg-slate-900/50 dark:text-white">
              {renderTaskStateList(todoItems, state?.todo_items_state, `${record?.id || 'todo'}-todo`)}
            </div>
            <div className="flex items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-xs font-bold shadow-sm dark:border-gray-700 dark:bg-slate-900/50">
              {renderStatusValue(record, { clickable: statusClickable })}
            </div>
            {showActions ? (
              <div className="flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white p-2 shadow-sm dark:border-gray-700 dark:bg-slate-900/50">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 rounded-full p-0 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700 dark:hover:bg-slate-800"
                  onClick={() => handleOpenEditIteneraryModal(record)}
                  aria-label={`Edit itinerary for ${record?.account_name || record?.accountName || 'record'}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 rounded-full p-0 text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-slate-800"
                  onClick={() => handleDeleteItenerary(record)}
                  aria-label={`Delete itinerary for ${record?.account_name || record?.accountName || 'record'}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
    );
  };

  useEffect(() => {
    if (!axiosInstance || !selectedEmployee?.id) {
      setIteneraryRecordStatesById({});
      setIsLoadingIteneraryStates(false);
      return undefined;
    }

    let isMounted = true;
    let isFetching = false;
    setIsLoadingIteneraryStates(true);

    const fetchStates = async () => {
      if (isFetching) {
        return;
      }
      isFetching = true;
      try {
        const params = { selected_employee_id: selectedEmployee.id };

        const response = await axiosInstance.get('/itineraries-records-state', { params });

        const list = Array.isArray(response?.data) ? response.data : [];
        const nextMap = list.reduce((acc, item) => {
          const key = String(item?.itinerary_record_id || item?.itenerary_record_id || '');
          if (key) {
            acc[key] = item;
          }
          return acc;
        }, {});

        if (isMounted) {
          setIteneraryRecordStatesById(nextMap);
        }
      } catch (error) {
        console.error('Error fetching Itenerary record states:', error);
        if (isMounted) {
          setIteneraryRecordStatesById({});
        }
      } finally {
        isFetching = false;
        if (isMounted) {
          setIsLoadingIteneraryStates(false);
        }
      }
    };

    fetchStates();
    const intervalId = setInterval(fetchStates, 5000);

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [axiosInstance, selectedEmployee?.id]);

  const handlePinLocation = useCallback((latlng) => {
    if (!latlng) {
      return;
    }
    setPinnedLatLng({ lat: latlng.lat, lng: latlng.lng });
    setLocationDraft(buildGoogleMapsLinkFromLatLng(latlng.lat, latlng.lng));
  }, []);

  useEffect(() => {
    if (!isLocationModalOpen) {
      return undefined;
    }

    let isActive = true;

    const setupMap = async () => {
      try {
        const L = await loadLeafletFromCdn();
        if (!isActive) {
          return;
        }

        const container = locationMapContainerRef.current;
        if (!container) {
          return;
        }

        const mapState = locationLeafletRef.current;
        if (!mapState.map) {
          const center = pinnedLatLng || DEFAULT_LAT_LNG;
          mapState.map = L.map(container, { zoomControl: true }).setView([center.lat, center.lng], DEFAULT_MAP_ZOOM);
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; OpenStreetMap contributors',
          }).addTo(mapState.map);
          mapState.map.on('click', (event) => {
            handlePinLocation(event.latlng);
          });
        } else {
          setTimeout(() => {
            mapState.map?.invalidateSize?.();
          }, 50);
        }

        if (pinnedLatLng) {
          if (!mapState.marker) {
            mapState.marker = L.marker([pinnedLatLng.lat, pinnedLatLng.lng]).addTo(mapState.map);
          } else {
            mapState.marker.setLatLng([pinnedLatLng.lat, pinnedLatLng.lng]);
          }
          mapState.map.setView([pinnedLatLng.lat, pinnedLatLng.lng], mapState.map.getZoom() || DEFAULT_MAP_ZOOM);
        }
      } catch (error) {
        console.error('Failed to load map viewer:', error);
      }
    };

    setupMap();

    return () => {
      isActive = false;
    };
  }, [handlePinLocation, isLocationModalOpen, pinnedLatLng]);

  const resetModalState = () => {
    setEmployeeSearchQuery('');
    setSelectedEmployeeIds([]);
  };

  const handleEmployeeModalOpenChange = (open) => {
    setIsEmployeeModalOpen(open);
    resetModalState();
  };

  const toggleEmployeeSelection = (employeeId) => {
    if (addedEmployeeIds.has(String(employeeId))) {
      return;
    }

    setSelectedEmployeeIds((currentIds) => {
      const normalizedId = String(employeeId);

      if (currentIds.includes(normalizedId)) {
        return currentIds.filter((id) => id !== normalizedId);
      }

      return [...currentIds, normalizedId];
    });
  };

  const handleAddSelectedEmployees = async () => {
    if (selectedEmployeeIds.length === 0 || !axiosInstance) {
      return;
    }

    const employeesToAdd = activeEmployees.filter((employee) => selectedEmployeeIds.includes(String(employee.id)));

    try {
      const results = await Promise.allSettled(
        employeesToAdd.map((employee) =>
          axiosInstance.post('/itineraries-selected-employees', {
            employee_ref: String(employee.id),
            employee_id: employee.employee_id || null,
            full_name: employee.full_name || 'Unnamed Employee',
            position: employee.position || null,
            branch: employee.branch || null,
            department: employee.department || null,
            email: employee.email || null,
          })
        )
      );

      const successfulRecords = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value.data);
      const failedCount = results.filter((result) => result.status === 'rejected').length;

      if (successfulRecords.length > 0) {
        setAddedEmployees((currentEmployees) => {
          const employeeMap = new Map(
            currentEmployees.map((employee) => [String(employee.employee_ref || employee.id), employee])
          );

          successfulRecords.forEach((employee) => {
            employeeMap.set(String(employee.employee_ref || employee.id), employee);
          });

          return Array.from(employeeMap.values());
        });

        toast.success(
          successfulRecords.length === 1
            ? 'Selected employee saved'
            : `${successfulRecords.length} selected employees saved`
        );
      }

      if (failedCount > 0) {
        toast.error(`Failed to save ${failedCount} selected employee(s)`);
      }

      handleEmployeeModalOpenChange(false);
    } catch (error) {
      console.error('Error saving Iteneraries selected employees:', error);
      toast.error('Failed to save selected employees');
    }
  };

  const handleRemoveEmployee = async (selectedEmployeeId) => {
    if (!axiosInstance) {
      return;
    }

    try {
      await axiosInstance.delete(`/itineraries-selected-employees/${selectedEmployeeId}`);
      setAddedEmployees((currentEmployees) => {
        const remainingEmployees = currentEmployees.filter((employee) => String(employee.id) !== String(selectedEmployeeId));
        setSelectedEmployee((currentSelected) => {
          if (!currentSelected) {
            return null;
          }
          if (String(currentSelected.id) === String(selectedEmployeeId)) {
            return null;
          }
          return currentSelected;
        });
        return remainingEmployees;
      });
      toast.success('Selected employee removed');
    } catch (error) {
      console.error('Error removing Iteneraries selected employee:', error);
      toast.error('Failed to remove selected employee');
    }
  };

  const resetIteneraryForm = useCallback(
    (dateValue = formatDateForValue(selectedDate || new Date())) => {
      setIteneraryForm(createInitialIteneraryForm(dateValue));
      setLocationDraft('');
      setIsDatePickerOpen(false);
      setIsLocationModalOpen(false);
    },
    [selectedDate]
  );

  const handleOpenIteneraryModal = () => {
    if (!selectedEmployee) {
      toast.error('Select an employee first');
      return;
    }
    setEditingIteneraryId(null);
    setEditingSelectedEmployeeId(null);
    resetIteneraryForm(formatDateForValue(selectedDate || new Date()));
    setIsIteneraryModalOpen(true);
  };


  const handleIteneraryModalOpenChange = (open) => {
    setIsIteneraryModalOpen(open);

    if (!open) {
      setEditingIteneraryId(null);
      setEditingSelectedEmployeeId(null);
      resetIteneraryForm(formatDateForValue(selectedDate || new Date()));
    }
  };

  const updateIteneraryField = (field, value) => {
    setIteneraryForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  };

  const updateListItem = (field, index, value) => {
    setIteneraryForm((currentForm) => ({
      ...currentForm,
      [field]: currentForm[field].map((item, itemIndex) => (itemIndex === index ? value : item)),
    }));
  };

  const addListItem = (field) => {
    setIteneraryForm((currentForm) => ({
      ...currentForm,
      [field]: [...currentForm[field], ''],
    }));
  };

  const handleIteneraryDateSelect = (date) => {
    const nextDateValue = formatDateForValue(date || new Date());
    updateIteneraryField('date', nextDateValue);
    setIsDatePickerOpen(false);
  };

  const handleOpenLocationModal = () => {
    const seededLocation = iteneraryForm.location || '';
    setLocationDraft(seededLocation);
    setPinnedLatLng(parseLatLngFromLocation(seededLocation));
    setIsLocationModalOpen(true);
  };

  const handleCalendarDateSelect = (date) => {
    const nextDate = date || new Date();
    setSelectedDate(nextDate);
    setActiveCalendarDate(nextDate);
    setIsDateAccountsModalOpen(true);
  };

  const handleOpenEditIteneraryModal = (record) => {
    if (!record) {
      return;
    }

    const appointmentParts = parseConfirmedAppointmentTime(record.confirmed_appointment_by_client);

    setEditingIteneraryId(record.id || null);
    setEditingSelectedEmployeeId(record.selected_employee_id || selectedEmployee?.id || null);
    setIteneraryForm({
      date: record.date || formatDateForValue(selectedDate || new Date()),
      accountName: record.account_name || record.accountName || '',
      confirmedAppointmentHour: appointmentParts.hour,
      confirmedAppointmentMinute: appointmentParts.minute,
      confirmedAppointmentPeriod: appointmentParts.period,
      address: record.address || '',
      location: record.location || '',
      priorities: Array.isArray(record.priorities) && record.priorities.length > 0 ? [...record.priorities] : [''],
      todoItems:
        Array.isArray(record.todo_items || record.todoItems) && (record.todo_items || record.todoItems).length > 0
          ? [...(record.todo_items || record.todoItems)]
          : [''],
    });
    setLocationDraft(record.location || '');
    setIsDatePickerOpen(false);
    setIsAppointmentTimePickerOpen(false);
    setIsLocationModalOpen(false);
    setIsDateAccountsModalOpen(false);
    setIsIteneraryModalOpen(true);
  };

  const handleSaveLocation = () => {
    updateIteneraryField('location', locationDraft.trim());
    setIsLocationModalOpen(false);
  };

  const handleSaveItenerary = async () => {
    if (!axiosInstance) {
      return;
    }

    if (!selectedEmployee) {
      toast.error('Select an employee first');
      return;
    }

    const trimmedAccountName = iteneraryForm.accountName.trim();
    if (!iteneraryForm.date) {
      toast.error('Date is required');
      return;
    }

    if (!trimmedAccountName) {
      toast.error('Account Name is required');
      return;
    }

    const priorities = iteneraryForm.priorities.map((item) => item.trim()).filter(Boolean);
    const todoItems = iteneraryForm.todoItems.map((item) => item.trim()).filter(Boolean);
    const confirmedAppointmentByClient = buildConfirmedAppointmentTime({
      hour: iteneraryForm.confirmedAppointmentHour,
      minute: iteneraryForm.confirmedAppointmentMinute,
      period: iteneraryForm.confirmedAppointmentPeriod,
    });

    setIsSavingItenerary(true);
    try {
      const response = await axiosInstance.post('/itineraries-records', {
        selected_employee_id: selectedEmployee.id,
        employee_ref: String(selectedEmployee.employee_ref || selectedEmployee.id),
        employee_id: selectedEmployee.employee_id || null,
        full_name: selectedEmployee.full_name || 'Unnamed Employee',
        date: iteneraryForm.date,
        account_name: trimmedAccountName,
        confirmed_appointment_by_client: confirmedAppointmentByClient || null,
        address: iteneraryForm.address.trim() || null,
        location: iteneraryForm.location.trim() || null,
        priorities,
        todo_items: todoItems,
      });

      const createdRecord = response?.data;
      if (createdRecord?.id) {
        setIteneraryRecords((currentRecords) => {
          const recordMap = new Map((Array.isArray(currentRecords) ? currentRecords : []).map((record) => [record.id, record]));
          recordMap.set(createdRecord.id, createdRecord);
          return Array.from(recordMap.values());
        });
      }

      toast.success('Itenerary saved successfully');
      setIsIteneraryModalOpen(false);
      resetIteneraryForm(formatDateForValue(selectedDate || new Date()));
    } catch (error) {
      console.error('Error saving Itenerary record:', error);
      toast.error('Failed to save Itenerary');
    } finally {
      setIsSavingItenerary(false);
    }
  };

  const handleUpdateItenerary = async () => {
    if (!axiosInstance) {
      return;
    }

    if (!selectedEmployee && !editingSelectedEmployeeId) {
      toast.error('Select an employee first');
      return;
    }

    if (!editingIteneraryId) {
      toast.error('Select an itinerary first');
      return;
    }

    const targetSelectedEmployeeId = editingSelectedEmployeeId || selectedEmployee?.id;
    if (!targetSelectedEmployeeId) {
      toast.error('Selected employee is required');
      return;
    }

    const trimmedAccountName = iteneraryForm.accountName.trim();
    if (!iteneraryForm.date) {
      toast.error('Date is required');
      return;
    }

    if (!trimmedAccountName) {
      toast.error('Account Name is required');
      return;
    }

    const priorities = iteneraryForm.priorities.map((item) => item.trim()).filter(Boolean);
    const todoItems = iteneraryForm.todoItems.map((item) => item.trim()).filter(Boolean);
    const confirmedAppointmentByClient = buildConfirmedAppointmentTime({
      hour: iteneraryForm.confirmedAppointmentHour,
      minute: iteneraryForm.confirmedAppointmentMinute,
      period: iteneraryForm.confirmedAppointmentPeriod,
    });

    setIsSavingItenerary(true);
    try {
      const response = await axiosInstance.put(`/itineraries-records/${editingIteneraryId}`, {
        selected_employee_id: targetSelectedEmployeeId,
        date: iteneraryForm.date,
        account_name: trimmedAccountName,
        confirmed_appointment_by_client: confirmedAppointmentByClient || null,
        address: iteneraryForm.address.trim() || null,
        location: iteneraryForm.location.trim() || null,
        priorities,
        todo_items: todoItems,
      });

      const updatedRecord = response?.data;
      if (updatedRecord?.id) {
        setIteneraryRecords((currentRecords) => {
          const recordMap = new Map((Array.isArray(currentRecords) ? currentRecords : []).map((record) => [record.id, record]));
          recordMap.set(updatedRecord.id, updatedRecord);
          return Array.from(recordMap.values());
        });
      }

      toast.success('Itinerary updated successfully');
      setIsIteneraryModalOpen(false);
      setEditingIteneraryId(null);
      setEditingSelectedEmployeeId(null);
      resetIteneraryForm(formatDateForValue(selectedDate || new Date()));
    } catch (error) {
      console.error('Error updating Itinerary record:', error);
      toast.error('Failed to update Itinerary');
    } finally {
      setIsSavingItenerary(false);
    }
  };

  const handleDeleteItenerary = async (record) => {
    if (!axiosInstance || !user?.is_super_admin) {
      return;
    }

    const recordId = String(record?.id || '').trim();
    if (!recordId) {
      toast.error('Itinerary record is missing an ID');
      return;
    }

    const accountName = String(record?.account_name || record?.accountName || 'this itinerary').trim() || 'this itinerary';
    const confirmed = window.confirm(`Delete ${accountName}?`);
    if (!confirmed) {
      return;
    }

    try {
      await axiosInstance.delete(`/itineraries-records/${recordId}`);

      setIteneraryRecords((currentRecords) =>
        (Array.isArray(currentRecords) ? currentRecords : []).filter((item) => String(item?.id || '') !== recordId)
      );
      setIteneraryRecordStatesById((currentStates) => {
        const nextStates = { ...(currentStates || {}) };
        delete nextStates[recordId];
        return nextStates;
      });

      if (String(editingIteneraryId || '') === recordId) {
        setIsIteneraryModalOpen(false);
        setEditingIteneraryId(null);
        setEditingSelectedEmployeeId(null);
        resetIteneraryForm(formatDateForValue(selectedDate || new Date()));
      }

      toast.success('Itinerary deleted successfully');
    } catch (error) {
      console.error('Error deleting Itinerary record:', error);
      toast.error(error?.response?.data?.detail || 'Failed to delete itinerary');
    }
  };

  const handleCycleCalendarView = () => {
    setCalendarViewMode((currentMode) => {
      if (currentMode === 'month') {
        return 'week';
      }
      if (currentMode === 'week') {
        return 'day';
      }
      return 'month';
    });
  };

  const handleCalendarPeriodShift = (direction) => {
    if (calendarViewMode === 'week') {
      setSelectedDate((currentDate) => addWeeks(currentDate, direction));
      return;
    }
    if (calendarViewMode === 'day') {
      setSelectedDate((currentDate) => addDays(currentDate, direction));
      return;
    }
    setSelectedDate((currentDate) => addMonths(currentDate, direction));
  };

  const handleCycleReportsMode = () => {
    setReportsMode((currentMode) => {
      if (currentMode === 'daily') {
        return 'weekly';
      }
      if (currentMode === 'weekly') {
        return 'monthly';
      }
      return 'daily';
    });
  };

  return (
    <>
      <div className="flex h-[calc(100vh-5.5rem)] min-h-0 w-full gap-4">
        <Card className="glass h-full min-h-0 basis-[20%] overflow-hidden border-0 shadow-lg">
          <CardContent className="flex h-full min-h-0 flex-col gap-4 overflow-hidden p-4">
            <div className="flex items-start justify-between gap-3">
              {user?.is_super_admin ? (
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => handleEmployeeModalOpenChange(true)}>
                  <Plus className="h-4 w-4" />
                  Add
                </Button>
              ) : (
                <div />
              )}
              <div className="text-right text-xs text-gray-500 dark:text-gray-300">
                {addedEmployees.length} added
              </div>
            </div>

            {user?.is_super_admin ? (
              <Button
                type="button"
                variant={showAllItineraries ? 'default' : 'outline'}
                className={showAllItineraries ? 'bg-green-600 hover:bg-green-700' : ''}
                onClick={() => setShowAllItineraries((current) => !current)}
              >
                Show All
              </Button>
            ) : null}

            {addedEmployees.length === 0 ? (
              <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-gray-300 px-3 py-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-300">
                No employees added yet.
              </div>
            ) : (
              <ScrollArea className="min-h-0 flex-1">
                <div className="space-y-3 pr-2">
                  {addedEmployees.map((employee) => (
                    <div
                      key={employee.id}
                      className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-transform duration-200 hover:-translate-y-1 hover:shadow-md ${
                        !showAllItineraries && String(selectedEmployee?.id) === String(employee.id)
                          ? 'border-green-500 bg-green-50/90 shadow-sm dark:border-green-500 dark:bg-green-900/20'
                          : 'border-gray-200 bg-white/60 dark:border-gray-700 dark:bg-slate-900/40'
                      }`}
                      onClick={() => {
                        setSelectedEmployee(employee);
                        setShowAllItineraries(false);
                      }}
                    >
                      <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">{employee.full_name || 'Unnamed Employee'}</div>
                          <div className={`mt-1 truncate text-xs ${!showAllItineraries && String(selectedEmployee?.id) === String(employee.id) ? 'text-green-700 dark:text-green-300' : 'text-gray-500 dark:text-gray-300'}`}>
                            {employee.position || 'No position'}
                            {' • '}
                            {employee.branch || 'No branch'}
                          </div>
                        </div>

                        <div className={`shrink-0 text-right text-xs font-medium ${!showAllItineraries && String(selectedEmployee?.id) === String(employee.id) ? 'text-green-700 dark:text-green-300' : 'text-gray-500 dark:text-gray-300'}`}>
                          {getEmailUsername(employee.email) || 'No username'}
                        </div>
                      </div>
                      {user?.is_super_admin ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 shrink-0 rounded-full p-0 text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-slate-800"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRemoveEmployee(employee.id);
                          }}
                          aria-label={`Remove ${employee.full_name || 'employee'}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        <Card className="glass h-full min-h-0 basis-[80%] overflow-hidden border-0 shadow-lg">
          <CardContent className="flex h-full min-h-0 flex-col gap-3 overflow-hidden p-4">
            <ScrollArea className="h-full pr-2">
              <div className="flex min-h-full flex-col gap-4 pb-1">
                <div className="flex min-h-[calc(100vh-12rem)] flex-col gap-3">
                  <div className="flex min-h-0 flex-1 items-stretch justify-stretch overflow-hidden">
                    {selectedEmployee || showAllItineraries ? (
                      <div className="flex h-full w-full min-h-0 flex-col gap-3">
                        <div className="flex items-center justify-between gap-3">
                          <Button type="button" variant="outline" onClick={handleCycleCalendarView}>
                            {calendarViewButtonLabel}
                          </Button>

                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => handleCalendarPeriodShift(-1)}
                              aria-label="Previous period"
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <div className="min-w-[220px] text-center text-lg font-bold text-gray-900 dark:text-white">
                              {calendarHeaderLabel}
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              onClick={() => handleCalendarPeriodShift(1)}
                              aria-label="Next period"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        <div className="flex min-h-0 flex-1 overflow-hidden">
                          {calendarViewMode === 'month' ? (
                            <Calendar
                              mode="single"
                              month={selectedDate}
                              selected={selectedDate}
                              onMonthChange={setSelectedDate}
                              onSelect={handleCalendarDateSelect}
                              hideNavigation
                              className="h-full w-full p-0 [&_.rdp]:p-0"
                              components={{ DayContent: calendarDayContent }}
                              classNames={{
                                months: "flex h-full w-full",
                                month: "flex h-full w-full min-h-0 max-w-none flex-col gap-3",
                                caption: "hidden",
                                nav: "hidden",
                                table: "flex w-full min-h-0 flex-1 flex-col",
                                head_row: "grid grid-cols-7 h-8",
                                head_cell: "text-muted-foreground w-full text-center font-normal text-sm",
                                tbody: "flex min-h-0 flex-1 flex-col gap-[3px]",
                                row: "grid min-h-0 flex-1 grid-cols-7 gap-[3px]",
                                cell: "relative h-full min-h-0 overflow-hidden p-0 text-center text-sm align-top",
                                day: "inline-flex h-full max-h-full w-full min-h-0 items-start justify-start overflow-hidden whitespace-nowrap rounded-lg border border-gray-200 bg-white px-2 py-1 text-base font-medium text-gray-900 shadow-sm transition-transform transition-shadow duration-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:-translate-y-0.5 hover:bg-accent hover:text-accent-foreground hover:shadow-md dark:border-gray-700 dark:bg-slate-900/50 dark:text-white font-normal aria-selected:opacity-100",
                                day_selected:
                                  "bg-sky-100 text-sky-900 border-sky-200 hover:bg-sky-200 hover:text-sky-900 focus:bg-sky-200 focus:text-sky-900 dark:bg-sky-900/30 dark:text-sky-100 dark:border-sky-700 dark:hover:bg-sky-900/40",
                              }}
                            />
                          ) : calendarViewMode === 'week' ? (
                            <div className="flex h-full w-full min-h-0 flex-col gap-3">
                              <div className="grid grid-cols-7 gap-[3px]">
                                {weekViewDates.map((date) => (
                                  <div
                                    key={`week-head-${formatDateForValue(date)}`}
                                    className="rounded-md px-2 py-2 text-center text-sm font-medium text-gray-600 dark:text-gray-300"
                                  >
                                    {new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date)}
                                  </div>
                                ))}
                              </div>
                              <div className="grid min-h-0 flex-1 grid-cols-7 gap-[3px]">
                                {weekViewDates.map((date) => {
                                  const dateKey = formatDateForValue(date);
                                  const records = iteneraryRecordsByDate.get(dateKey) || [];
                                  const isSelected = dateKey === formatDateForValue(selectedDate);

                                  return (
                                    <button
                                      key={dateKey}
                                      type="button"
                                      onClick={() => handleCalendarDateSelect(date)}
                                      className={`flex h-full min-h-0 flex-col overflow-hidden rounded-lg border px-2 py-1 text-left shadow-sm transition-transform transition-shadow duration-200 hover:-translate-y-0.5 hover:shadow-md ${
                                        isSelected
                                          ? 'border-sky-200 bg-sky-100 text-sky-900 dark:border-sky-700 dark:bg-sky-900/30 dark:text-sky-100'
                                          : 'border-gray-200 bg-white text-gray-900 dark:border-gray-700 dark:bg-slate-900/50 dark:text-white'
                                      }`}
                                    >
                                      <div className="shrink-0 text-base font-medium">{date.getDate()}</div>
                                      <div className="mt-1 min-h-0 flex-1 overflow-y-auto text-[10px] leading-tight text-gray-600 dark:text-gray-300">
                                        {records.length > 0 ? records.map((record, index) => (
                                          <div key={`${dateKey}-record-${index}`} className="break-words whitespace-normal">
                                            {formatAccountDisplayName(record)}
                                          </div>
                                        )) : null}
                                      </div>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <ScrollArea className="h-full w-full pr-2">
                              <div className="space-y-3">
                                {dayViewTimedRecords.unscheduled.length > 0 ? (
                                  <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-900/10">
                                    <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                                      No Confirmed Appointment Time
                                    </div>
                                    <div className="mt-2 space-y-2">
                                      {dayViewTimedRecords.unscheduled.map((record, index) => (
                                        <button
                                          key={`unscheduled-${record?.id || index}`}
                                          type="button"
                                          onClick={() => handleCalendarDateSelect(selectedDate)}
                                          className="w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-left text-sm text-gray-900 shadow-sm hover:bg-amber-100 dark:border-amber-800 dark:bg-slate-900/50 dark:text-white"
                                        >
                                          {formatAccountDisplayName(record)}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                ) : null}

                                <div className="space-y-2">
                                  {dayViewTimedRecords.timeline.map((slot) => (
                                    <div
                                      key={`timeline-${slot.hour}`}
                                      className="grid grid-cols-[90px_1fr] gap-3 rounded-lg border border-gray-200 bg-white/70 p-3 dark:border-gray-700 dark:bg-slate-900/40"
                                    >
                                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-300">
                                        {formatDayTimelineLabel(slot.hour)}
                                      </div>
                                      <div className="min-h-[2.5rem] space-y-2">
                                        {slot.items.map((record, index) => (
                                          <button
                                            key={`timeline-item-${record?.id || index}`}
                                            type="button"
                                            onClick={() => handleCalendarDateSelect(selectedDate)}
                                            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm text-gray-900 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-slate-900/60 dark:text-white dark:hover:bg-slate-900"
                                          >
                                            <div className="font-semibold">
                                              {formatAccountDisplayName(record)}
                                            </div>
                                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-300">
                                              {record?.confirmed_appointment_by_client || 'No time'}
                                            </div>
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </ScrollArea>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex h-full w-full items-center justify-center rounded-lg border border-dashed border-gray-300 text-center text-sm font-medium text-gray-500 dark:border-gray-700 dark:text-gray-300">
                        No Selected Sales Account
                      </div>
                    )}
                  </div>

                  <div className="flex flex-shrink-0 items-center justify-start gap-3">
                    <Button
                      type="button"
                      className="bg-indigo-600 hover:bg-indigo-700"
                      onClick={handleOpenIteneraryModal}
                      disabled={!selectedEmployee || showAllItineraries}
                    >
                      Add Itenerary
                    </Button>
                    {isLoadingIteneraries ? (
                      <div className="text-xs text-gray-500 dark:text-gray-300">Loading…</div>
                    ) : null}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-slate-900/50">
                  <div className="text-center text-lg font-bold text-gray-900 dark:text-white">Status</div>
                  <div className="mt-4">
                    {selectedEmployee ? (
                      <div className="space-y-4">
                        <div className="text-center text-lg font-bold text-gray-900 dark:text-white">Current Itineraries</div>
                        <div className="text-center text-sm text-gray-600 dark:text-gray-300">{currentDateDisplay}</div>
                        {isLoadingIteneraryStates ? (
                          <div className="text-center text-xs text-gray-500 dark:text-gray-300">Syncing status…</div>
                        ) : null}

                        {currentDateIteneraries.length > 0 ? (
                          renderItineraryStatusTable(currentDateIteneraries)
                        ) : (
                          <div className="flex min-h-[12rem] items-center justify-center rounded-lg border border-dashed border-gray-300 px-3 py-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-300">
                            No itineraries found for today.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex min-h-[12rem] items-center justify-center rounded-lg border border-dashed border-gray-300 px-3 py-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-300">
                        No Selected Sales Account
                      </div>
                    )}
                  </div>
                </div>

                  <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-slate-900/50">
                  <div className="text-center text-lg font-bold text-gray-900 dark:text-white">Reports</div>
                  <div className="mt-4">
                    {selectedEmployee ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              className="h-9 justify-start border-gray-200 bg-white text-sm text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:bg-slate-900/50 dark:text-white dark:hover:bg-slate-900"
                              onClick={handleCycleReportsMode}
                            >
                              {reportsModeButtonLabel}
                            </Button>
                            <Popover open={isReportsCalendarOpen} onOpenChange={setIsReportsCalendarOpen}>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-9 w-9 border-gray-200 bg-white text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:bg-slate-900/50 dark:text-white dark:hover:bg-slate-900"
                                  aria-label={`Select ${reportsModeButtonLabel}`}
                                >
                                  <CalendarDays className="h-4 w-4 text-gray-500 dark:text-gray-300" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-2" align="start">
                                <Calendar
                                  mode="single"
                                  selected={recordsFilterDate}
                                  month={recordsFilterDate}
                                  modifiers={{ reportRange: { from: reportsRange.from, to: reportsRange.to } }}
                                  modifiersClassNames={{
                                    reportRange: 'bg-sky-100 text-sky-900 dark:bg-sky-900/30 dark:text-sky-100',
                                  }}
                                  onSelect={(value) => {
                                    if (value) {
                                      setRecordsFilterDate(value);
                                      setIsReportsCalendarOpen(false);
                                    }
                                  }}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>

                          <div className="text-right">
                            <div className="text-sm font-semibold text-gray-900 dark:text-white">{reportsPeriodLabel}</div>
                            {isLoadingIteneraryStates ? (
                              <div className="text-xs text-gray-500 dark:text-gray-300">Syncing…</div>
                            ) : null}
                          </div>
                        </div>

                        {reportsFilteredIteneraries.length > 0 ? (
                          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[360px_minmax(0,1fr)]">
                            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-slate-900/50">
                              <div className="text-sm font-semibold text-gray-900 dark:text-white">Summary</div>
                              <div className="mt-3 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                                <div className="flex items-center justify-between gap-3">
                                  <span>Account Names</span>
                                  <span className="font-semibold text-gray-900 dark:text-white">{reportsMetrics.accountCount}</span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <span>Priorities</span>
                                  <span className="font-semibold text-gray-900 dark:text-white">{reportsMetrics.totalPriorities}</span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <span>To-Do</span>
                                  <span className="font-semibold text-gray-900 dark:text-white">{reportsMetrics.totalTodoItems}</span>
                                </div>
                                <div className="flex items-center justify-between gap-3">
                                  <span>Cancelled</span>
                                  <span className="font-semibold text-red-600">{reportsMetrics.cancelledCount}</span>
                                </div>
                              </div>

                              <div className="mt-4 rounded-lg bg-gray-50 px-3 py-3 text-sm dark:bg-slate-900">
                                <div className="flex items-center justify-between gap-3">
                                  <span className="text-gray-700 dark:text-gray-300">Finished</span>
                                  <span className="font-bold text-green-600">{Math.round(reportsMetrics.finishedPct)}%</span>
                                </div>
                                <div className="mt-1 flex items-center justify-between gap-3">
                                  <span className="text-gray-700 dark:text-gray-300">Cancelled</span>
                                  <span className="font-bold text-red-600">{Math.round(reportsMetrics.cancelledPct)}%</span>
                                </div>
                              </div>
                            </div>

                            <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-slate-900/50">
                              <div className="text-center text-sm font-semibold text-gray-900 dark:text-white">
                                Report Summary
                              </div>
                              <div className="mt-2 h-[320px] w-full">
                                <div
                                  key={reportsChartKey}
                                  className={`relative h-full w-full ${reportsPieTotal === 0 ? 'animate-pulse' : ''}`}
                                >
                                <ResponsiveContainer width="100%" height="100%">
                                  <PieChart>
                                    <Pie
                                      data={reportsChartData}
                                      dataKey="value"
                                      nameKey="name"
                                      innerRadius={0}
                                      outerRadius="78%"
                                      paddingAngle={3}
                                      labelLine={false}
                                      isAnimationActive
                                      animationDuration={900}
                                      label={({ payload, value }) => (payload?.name === 'No Data' ? '' : value)}
                                    >
                                      {reportsChartData.map((entry, index) => (
                                        <Cell
                                          key={`${entry.name}-${index}`}
                                          fill={
                                            entry.name === 'Cancelled'
                                              ? '#dc2626'
                                              : entry.name === 'Finished'
                                                ? '#16a34a'
                                                : '#cbd5e1'
                                          }
                                          className={entry.name === 'Cancelled' ? 'cursor-pointer' : undefined}
                                          onClick={() => {
                                            if (entry.name !== 'Cancelled') {
                                              return;
                                            }
                                            if (reportsMetrics.cancelledItems.length === 0) {
                                              toast.error('No cancellations recorded');
                                              return;
                                            }
                                            setIsCancelledRecordsModalOpen(true);
                                          }}
                                        />
                                      ))}
                                    </Pie>
                                  </PieChart>
                                </ResponsiveContainer>
                                {reportsPieTotal === 0 ? (
                                  <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                    <div className="flex h-24 w-24 items-center justify-center rounded-full border-8 border-slate-200 bg-white text-xl font-bold text-gray-400 dark:border-slate-700 dark:bg-slate-900/80 dark:text-gray-500">
                                      0%
                                    </div>
                                  </div>
                                ) : null}
                                </div>
                              </div>
                              <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-300">
                                {reportsPieData.map((entry) => (
                                  <div key={entry.name} className="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-2 py-1 dark:bg-slate-900">
                                    <span className="truncate">{entry.name}</span>
                                    <span className="font-semibold text-gray-900 dark:text-white">{entry.value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="flex min-h-[12rem] items-center justify-center rounded-lg border border-dashed border-gray-300 px-3 py-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-300">
                            No itineraries found for this report range.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex min-h-[12rem] items-center justify-center rounded-lg border border-dashed border-gray-300 px-3 py-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-300">
                        No Selected Sales Account
                      </div>
                    )}
                  </div>
                </div>

                  <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-slate-900/50">
                  <div className="text-center text-lg font-bold text-gray-900 dark:text-white">Records</div>
                  <div className="mt-4">
                    {selectedEmployee ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between gap-3">
                          <Popover open={isRecordsMultiDatePickerOpen} onOpenChange={setIsRecordsMultiDatePickerOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="outline"
                                className="h-9 justify-start gap-2 border-gray-200 bg-white text-sm text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:bg-slate-900/50 dark:text-white dark:hover:bg-slate-900"
                              >
                                <CalendarDays className="h-4 w-4 text-gray-500 dark:text-gray-300" />
                                {selectedRecordDateKeys.length > 0 ? `${selectedRecordDateKeys.length} date(s)` : 'All Dates'}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-2" align="start">
                              <Calendar
                                mode="multiple"
                                selected={recordsSelectedDates}
                                onSelect={(value) => setRecordsSelectedDates(Array.isArray(value) ? value : [])}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                          <div className="flex-1 text-center">
                            <div className="text-lg font-bold text-gray-900 dark:text-white">Itinerary Records</div>
                            <div className="text-sm text-gray-600 dark:text-gray-300">All saved itinerary records for the selected employee</div>
                          </div>
                          <div className="w-[96px] text-right text-xs text-gray-500 dark:text-gray-300">
                            {selectedRecordDateKeys.length > 0 ? 'Filtered' : 'All dates'}
                          </div>
                        </div>
                        {isLoadingIteneraryStates ? (
                          <div className="text-center text-xs text-gray-500 dark:text-gray-300">Syncing status…</div>
                        ) : null}

                        {filteredRecordsIteneraries.length > 0 ? (
                          renderItineraryStatusTable(filteredRecordsIteneraries, {
                            showDate: true,
                            statusClickable: true,
                            showActions: Boolean(user?.is_super_admin),
                          })
                        ) : (
                          <div className="flex min-h-[12rem] items-center justify-center rounded-lg border border-dashed border-gray-300 px-3 py-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-300">
                            No itinerary records found for the selected dates.
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex min-h-[12rem] items-center justify-center rounded-lg border border-dashed border-gray-300 px-3 py-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-300">
                        No Selected Sales Account
                      </div>
                    )}
                  </div>
                </div>

                  {user?.is_super_admin ? (
                    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-slate-900/50">
                      <div className="text-center text-lg font-bold text-gray-900 dark:text-white">Allowance</div>
                      <div className="mt-4">
                        <div className="flex min-h-[12rem] items-center justify-center rounded-lg border border-dashed border-gray-300 px-3 py-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-300">
                          Content coming soon.
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      <Dialog open={isEmployeeModalOpen} onOpenChange={handleEmployeeModalOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-hidden">
          <DialogHeader>
            <DialogTitle>Add Employees</DialogTitle>
            <DialogDescription>Select active employees enrolled in the attendance monitoring system.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 dark:text-gray-300" />
              <Input
                value={employeeSearchQuery}
                onChange={(event) => setEmployeeSearchQuery(event.target.value)}
                placeholder="Search by name, ID, or email"
                className="pl-9"
              />
            </div>

            <ScrollArea className="h-80 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="space-y-2 p-3">
                {filteredEmployees.length === 0 ? (
                  <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-300">
                    {activeEmployees.length === 0 ? 'No active employees found.' : 'No employees match your search.'}
                  </div>
                ) : (
                  filteredEmployees.map((employee) => {
                    const isSelected = selectedEmployeeIds.includes(employee.id);
                    const isAdded = addedEmployeeIds.has(String(employee.id));

                    return (
                      <div
                        key={employee.id}
                        className={`flex items-center gap-3 rounded-lg border px-3 py-3 ${
                          isAdded
                            ? 'border-gray-200 bg-gray-100 opacity-70 dark:border-gray-700 dark:bg-slate-800'
                            : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-slate-900/40'
                        }`}
                      >
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-9 w-9 rounded-full border border-gray-300 p-0 hover:bg-green-50 dark:border-gray-600 dark:hover:bg-slate-800"
                          onClick={() => toggleEmployeeSelection(employee.id)}
                          disabled={isAdded}
                          aria-label={isSelected ? `Deselect ${employee.full_name}` : `Select ${employee.full_name}`}
                        >
                          {isSelected ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          ) : (
                            <Circle className="h-5 w-5 text-gray-400" />
                          )}
                        </Button>

                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">{employee.full_name || 'Unnamed Employee'}</div>
                          <div className="truncate text-xs text-gray-500 dark:text-gray-300">
                            {employee.employee_id || employee.id}
                            {employee.email ? ` • ${employee.email}` : ''}
                          </div>
                        </div>

                        {isAdded && <div className="text-xs font-medium text-green-600 dark:text-green-400">Added</div>}
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleEmployeeModalOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-indigo-600 hover:bg-indigo-700"
              onClick={handleAddSelectedEmployees}
              disabled={selectedEmployeeIds.length === 0}
            >
              Add Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isDateAccountsModalOpen}
        onOpenChange={(open) => {
          setIsDateAccountsModalOpen(open);
          if (!open) {
            setActiveCalendarDate(null);
          }
        }}
      >
        <DialogContent className="max-w-md border border-slate-200 bg-white text-gray-900 shadow-2xl duration-300 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 dark:bg-white dark:text-gray-900">
          <DialogHeader className="items-start text-left">
            <DialogTitle className="text-5xl font-bold leading-none text-slate-900">
              {activeCalendarDate ? activeCalendarDate.getDate() : ''}
            </DialogTitle>
          </DialogHeader>

          <div className="min-h-0 flex-1">
            {activeCalendarDateRecords.length > 0 ? (
              <ScrollArea className="max-h-[50vh] pr-4">
                <div className="flex min-h-[8rem] flex-col items-center justify-center gap-2 text-center">
                  {activeCalendarDateRecords.map((record, index) => (
                    showAllItineraries ? (
                      <div
                        key={`${record?.id || activeCalendarDateKey}-${index}`}
                        className="text-sm font-medium text-slate-800"
                      >
                        {formatAccountDisplayName(record)}
                      </div>
                    ) : (
                      <button
                        key={`${record?.id || activeCalendarDateKey}-${index}`}
                        type="button"
                        className="text-sm font-medium text-slate-800 hover:underline"
                        onClick={() => handleOpenEditIteneraryModal(record)}
                      >
                        {formatAccountDisplayName(record)}
                      </button>
                    )
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="flex min-h-[8rem] items-center justify-center text-center text-sm text-slate-500">
                No account names saved for this date.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isIteneraryModalOpen} onOpenChange={handleIteneraryModalOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-hidden">
          <DialogHeader>
            <DialogTitle>Itinerary Details Form</DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[65vh] pr-4">
            <div className="space-y-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">Date</div>
                  <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" size="sm" className="gap-2">
                        <CalendarDays className="h-4 w-4" />
                        {formatDateForDisplay(iteneraryForm.date)}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={iteneraryForm.date ? new Date(`${iteneraryForm.date}T00:00:00`) : undefined}
                        onSelect={handleIteneraryDateSelect}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <Input value={formatDateForDisplay(iteneraryForm.date)} readOnly />
              </div>

              <div className="space-y-2">
                <div className="text-sm font-semibold text-gray-900 dark:text-white">Account Name</div>
                <Input
                  value={iteneraryForm.accountName}
                  onChange={(event) => updateIteneraryField('accountName', event.target.value)}
                  placeholder="Enter account name"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">Confirmed Appointment By Client</div>
                  <Popover open={isAppointmentTimePickerOpen} onOpenChange={setIsAppointmentTimePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" size="sm" className="gap-2">
                        <Clock className="h-4 w-4" />
                        {buildConfirmedAppointmentTime({
                          hour: iteneraryForm.confirmedAppointmentHour,
                          minute: iteneraryForm.confirmedAppointmentMinute,
                          period: iteneraryForm.confirmedAppointmentPeriod,
                        }) || 'Select time'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-[320px] p-3">
                      <div className="grid grid-cols-3 gap-2">
                        <Select
                          value={String(iteneraryForm.confirmedAppointmentHour || '')}
                          onValueChange={(value) => updateIteneraryField('confirmedAppointmentHour', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Hour" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 12 }, (_, idx) => `${idx + 1}`).map((value) => (
                              <SelectItem key={`hour-${value}`} value={value}>
                                {value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select
                          value={String(iteneraryForm.confirmedAppointmentMinute || '00')}
                          onValueChange={(value) => updateIteneraryField('confirmedAppointmentMinute', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Minute" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: 60 }, (_, idx) => `${idx}`.padStart(2, '0')).map((value) => (
                              <SelectItem key={`minute-${value}`} value={value}>
                                {value}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Select
                          value={String(iteneraryForm.confirmedAppointmentPeriod || 'AM')}
                          onValueChange={(value) => updateIteneraryField('confirmedAppointmentPeriod', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="AM/PM" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="AM">AM</SelectItem>
                            <SelectItem value="PM">PM</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <Input
                  readOnly
                  value={
                    buildConfirmedAppointmentTime({
                      hour: iteneraryForm.confirmedAppointmentHour,
                      minute: iteneraryForm.confirmedAppointmentMinute,
                      period: iteneraryForm.confirmedAppointmentPeriod,
                    }) || ''
                  }
                  placeholder="HH:MM AM"
                />
              </div>

              <div className="space-y-2">
                <div className="text-sm font-semibold text-gray-900 dark:text-white">Addres</div>
                <Input
                  value={iteneraryForm.address}
                  onChange={(event) => updateIteneraryField('address', event.target.value)}
                  placeholder="Enter address"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">Location</div>
                  <Button type="button" variant="outline" size="sm" className="gap-2" onClick={handleOpenLocationModal}>
                    <Globe className="h-4 w-4" />
                    Open Map
                  </Button>
                </div>
                <Input
                  value={iteneraryForm.location}
                  onChange={(event) => updateIteneraryField('location', event.target.value)}
                  placeholder="Paste the selected Google Maps link"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">Priorities</div>
                  <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => addListItem('priorities')}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {iteneraryForm.priorities.map((priority, index) => (
                    <div key={`priority-${index + 1}`} className="flex items-center gap-3">
                      <div className="w-8 text-sm font-medium text-gray-500 dark:text-gray-300">{index + 1}.</div>
                      <Input
                        value={priority}
                        onChange={(event) => updateListItem('priorities', index, event.target.value)}
                        placeholder={`Priority ${index + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">To-Do</div>
                  <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => addListItem('todoItems')}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="space-y-2">
                  {iteneraryForm.todoItems.map((todoItem, index) => (
                    <div key={`todo-${index + 1}`} className="flex items-center gap-3">
                      <div className="w-8 text-sm font-medium text-gray-500 dark:text-gray-300">{index + 1}.</div>
                      <Input
                        value={todoItem}
                        onChange={(event) => updateListItem('todoItems', index, event.target.value)}
                        placeholder={`To-Do ${index + 1}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleIteneraryModalOpenChange(false)} disabled={isSavingItenerary}>
              Cancel
            </Button>
            {editingIteneraryId ? (
              <Button type="button" className="bg-indigo-600 hover:bg-indigo-700" onClick={handleUpdateItenerary} disabled={isSavingItenerary}>
                Update
              </Button>
            ) : (
              <Button type="button" className="bg-indigo-600 hover:bg-indigo-700" onClick={handleSaveItenerary} disabled={isSavingItenerary}>
                Save
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isLocationModalOpen} onOpenChange={setIsLocationModalOpen}>
        <DialogContent className="max-w-4xl overflow-hidden">
          <DialogHeader>
            <DialogTitle>Location</DialogTitle>
            <DialogDescription>Click the map to drop a pin. The latitude and longitude will appear below, and the Location link will be auto-filled.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700">
              <div ref={locationMapContainerRef} className="h-[420px] w-full" />
            </div>

            <div className="flex items-center justify-center gap-8 text-sm text-gray-700 dark:text-gray-200">
              <div>
                Latitude: <span className="font-medium">{pinnedLatLng ? formatCoord(pinnedLatLng.lat) : '--'}</span>
              </div>
              <div>
                Longitude: <span className="font-medium">{pinnedLatLng ? formatCoord(pinnedLatLng.lng) : '--'}</span>
              </div>
            </div>

            <Input
              value={locationDraft}
              onChange={(event) => setLocationDraft(event.target.value)}
              placeholder="Location link (auto-filled on pin drop)"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsLocationModalOpen(false)}>
              Cancel
            </Button>
            <Button type="button" className="bg-indigo-600 hover:bg-indigo-700" onClick={handleSaveLocation}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isCancelledRecordsModalOpen} onOpenChange={setIsCancelledRecordsModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Reason of Cancellation</DialogTitle>
            <DialogDescription>
              {reportsPeriodLabel}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="h-80 pr-2">
            <div className="space-y-3">
              {reportsMetrics.cancelledItems.map((item) => (
                <div key={item.id} className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-slate-900/50">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                    <div className="min-w-0 truncate">{item.accountName || 'Unnamed account'}</div>
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-300">
                      {item.date ? formatLongDateForDisplay(`${item.date}T00:00:00`) : ''}
                    </div>
                  </div>
                  <div className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-700 dark:text-gray-300">
                    {item.reason || '-'}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsCancelledRecordsModalOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(selectedStatusDetail)} onOpenChange={(open) => { if (!open) setSelectedStatusDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>{selectedStatusDetail?.title || 'Itinerary Detail'}</DialogTitle>
            <DialogDescription>
              {selectedStatusDetail?.accountName || ''}
              {selectedStatusDetail?.date ? ` • ${selectedStatusDetail.date}` : ''}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh] pr-2">
            <div className="whitespace-pre-wrap break-words rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-700 dark:border-gray-700 dark:bg-slate-900/50 dark:text-gray-300">
              {selectedStatusDetail?.content || '-'}
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setSelectedStatusDetail(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
